import type { Except } from '@festina-life/core'
import { type ComponentProps, useEffect, useRef, useState } from 'react'

import { useTranslate } from '../../hooks'
import { IconButton } from '../iconButton/IconButton'

const ANIMATION_DELAY = 300

type IconButtonProps = ComponentProps<typeof IconButton>
type IconClickEvent = Parameters<NonNullable<IconButtonProps['onClick']>>[0]

type Props = Except<
  IconButtonProps,
  'type' | 'isSelected' | 'isLoading' | 'isDanger' | 'tip' | 'iconName'
> & {
  copyValue: string
  onCopied?: (target: EventTarget & HTMLButtonElement) => void
}

/**
 * Component for copying a value to clipboard, with visual feedback when copied.
 */
export const CopyToClipboard = ({
  copyValue,
  onCopied,
  ...restProps
}: Props) => {
  const translate = useTranslate()

  const [isCopying, setIsCopying] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clear any existing timeout
  const clearTimeoutRef = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }

  const handleCopy = (event: IconClickEvent) => {
    clearTimeoutRef()

    const target = event.currentTarget
    event.stopPropagation() // if the consumer needs bubling they can do that onCopied with the target
    setIsCopying(true)

    void navigator.clipboard.writeText(copyValue).then(() => {
      timeoutRef.current = setTimeout(() => {
        onCopied?.(target)
        setIsCopying(false)
      }, ANIMATION_DELAY)
    })
  }

  // Cleanup on unmount
  useEffect(() => clearTimeoutRef, [])

  return (
    <IconButton
      tip={translate('copy')}
      iconName={isCopying ? 'Check' : 'DocumentDuplicate'}
      onClick={handleCopy}
      {...restProps} // IconButton handles safe rest props
    />
  )
}
